import { FxAlert } from '@/components/fx'
import { useUsage } from '@/hooks/useUsage'
import type { LimitRefusal } from '@/lib/api/billing'

/**
 * What is left on the plan, shown before the person commits to anything.
 *
 * Renders nothing when the deployment enforces no limits, or when the plan is
 * unlimited: a quota line that always says "unbegrenzt" is noise.
 *
 * `voice` exists because this component is shown on both sides of the
 * register screen: the signed-in app says "Sie", the pre-account funnel says
 * "du" throughout, and one sentence switching register mid-flow is the thing
 * a reader notices. Same wording either way — `limitCopy` in
 * `pages/onboarding/analyseProgress.ts` is its counterpart for a refusal that
 * has already happened.
 */
export function PlanQuota({
  kind = 'pages',
  voice = 'sie',
}: {
  kind?: 'pages' | 'templates'
  voice?: 'sie' | 'du'
}) {
  const usage = useUsage()
  const data = usage.data

  if (!data?.enforced || !data.usage) return null

  if (kind === 'templates') {
    const { templates, maxTemplates } = data.usage
    if (maxTemplates < 0) return null
    if (templates >= maxTemplates) {
      return voice === 'du' ? (
        <FxAlert tone="warning" title="Dein Tarif enthält ein Formular">
          Du hast {templates} von {maxTemplates} Formularen eingerichtet. Für ein weiteres brauchst
          du einen anderen Tarif — oder du löschst ein bestehendes.
        </FxAlert>
      ) : (
        <FxAlert tone="warning" title="Ihr Tarif enthält ein Formular">
          Sie haben {templates} von {maxTemplates} Formularen eingerichtet. Für ein weiteres
          brauchen Sie einen anderen Tarif — oder Sie löschen ein bestehendes.
        </FxAlert>
      )
    }
    return null
  }

  const { pages, maxPages, pagesRemaining } = data.usage
  if (maxPages < 0 || pagesRemaining == null) return null

  if (pagesRemaining === 0) {
    return voice === 'du' ? (
      <FxAlert tone="warning" title="Dein Kontingent ist aufgebraucht">
        {pages} von {maxPages} Seiten verbraucht. Was du schon hochgeladen hast, bleibt vollständig
        nutzbar — lesen, prüfen und exportieren geht weiter. Für neue Seiten lösch ältere oder
        wechsle den Tarif.
      </FxAlert>
    ) : (
      <FxAlert tone="warning" title="Ihr Kontingent ist aufgebraucht">
        {pages} von {maxPages} Seiten verbraucht. Was Sie schon hochgeladen haben, bleibt
        vollständig nutzbar — lesen, prüfen und exportieren geht weiter. Für neue Seiten löschen Sie
        ältere oder wechseln den Tarif.
      </FxAlert>
    )
  }

  return (
    <p className="text-small text-text-muted">
      {pages} von {maxPages} Seiten verbraucht — {pagesRemaining} übrig.
    </p>
  )
}

/** The message for a refusal that already happened. */
export function limitMessage(refusal: LimitRefusal): { title: string; body: string } {
  if (refusal.code === 'template_limit_reached') {
    return {
      title: 'Ihr Tarif enthält ein Formular',
      body: `Sie haben ${refusal.used} von ${refusal.limit} Formularen eingerichtet. Löschen Sie ein bestehendes oder wechseln Sie den Tarif.`,
    }
  }
  return {
    title: 'Ihr Kontingent ist aufgebraucht',
    body: `${refusal.used} von ${refusal.limit} Seiten verbraucht. Alles bereits Hochgeladene bleibt nutzbar; für neue Seiten löschen Sie ältere oder wechseln den Tarif.`,
  }
}
